/**
 * The pure, server-authoritative advance call — extracted from
 * `WorkflowTrackerBody.tsx`'s `approveViaServer` (task 3 of the
 * workflow-panel-run-contract plan) so both the drawer footer (page path)
 * and the panel's inline approval row call the exact same durable
 * `/api/workflow/advance` transition instead of two implementations
 * drifting apart. Behavior-preserving: same endpoint, same body shape, same
 * result discriminants. The caller owns dispatch guards, toasts, the
 * completion celebration, and the pending-schedule dialog decision — those
 * are presentation concerns, not transport ones.
 */

export type AdvanceWorkflowStepResult =
  { result: 'advanced' } | { result: 'completed' } | { result: 'needs_schedule'; message: string };

export class AdvanceWorkflowStepError extends Error {}

export async function advanceWorkflowStep(params: {
  projectId: string;
  fromStepId: number;
  action: 'approve';
  accessToken: string | undefined;
}): Promise<AdvanceWorkflowStepResult> {
  const res = await fetch('/api/workflow/advance', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken ?? ''}`,
    },
    body: JSON.stringify({
      projectId: params.projectId,
      fromStepId: params.fromStepId,
      action: params.action,
    }),
  });

  const data = (await res.json().catch(() => ({}))) as {
    result?: string;
    error?: string;
    message?: string;
  };

  if (!res.ok) {
    throw new AdvanceWorkflowStepError(data?.error ?? 'Could not advance the workflow. Try again.');
  }
  if (data.result === 'needs_schedule') {
    return {
      result: 'needs_schedule',
      message: data.message ?? 'These posts are not scheduled yet — finish scheduling first.',
    };
  }
  if (data.result === 'completed') return { result: 'completed' };
  return { result: 'advanced' };
}
